import {
  faArrowRight,
  faGavel,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { AlertOctagon } from "react-feather";
import toast from "react-hot-toast";

import Table from "../../../../components/tables/Table";

import listingService from "../../../../services/listingService";
import printService from "../../../../services/print.service";

const AuctionToBin = ({ submitting, setSubmitting }) => {
  const [error, setError] = useState({});
  const [toggle, setToggle] = useState(true);

  const [value, setValue] = useState("");
  const [skuListings, setSkuListings] = useState([]);

  const [validationErrors, setValidationErrors] = useState({});

  const inputRef = useRef(null);

  useEffect(() => {
    if (skuListings) {
      setToggle(true);
      setSkuListings([]);
    }
  }, [value]);

  const handleSubmit = async (listingId) => {
    setError({});
    setValidationErrors({});
    if (!validateForm()) {
      return;
    }

    // Decipher what type of input it is.
    const listingIdRegex = /^\d{7}$/;
    const auctionIdRegex = /^RWW(\d{7})$/i;
    const skuRegex = /^([a-z0-9]{10})-([a-z]+)-([a-z]+)$/i;

    let inputType;
    if (listingIdRegex.test(value)) {
      inputType = "LISTING_ID";
    } else if (auctionIdRegex.test(value)) {
      inputType = "AUCTION_ID";
    } else if (skuRegex.test(value)) {
      inputType = "SKU";
    } else {
      setValidationErrors((prevState) => {
        return {
          ...prevState,
          input: `"${value}" must be a Listing ID, Auction ID, or SKU. Check help section for clarification.`,
        };
      });
      return;
    }

    setSubmitting(true);

    if (inputType === "SKU" && !listingId) {
      const result = await listingService.getListingItemsBySku({
        conversionType: "AUC_TO_BIN",
        sku: value,
      });

      if (result.success) {
        setSkuListings(result.listings);
        setToggle(false);
      } else {
        setError(result);
      }

      setSubmitting(false);
      return;
    }

    if (inputType === "LISTING_ID") {
      listingId = value;
    } else if (inputType === "AUCTION_ID") {
      listingId = value.match(auctionIdRegex)[1];
    }

    const response = await listingService.convertListing({
      conversionType: "AUC_TO_BIN",
      listingId,
    });

    if (response.success) {
      toast.success("Listing successfully converted!");

      await printService.printListingLabel({
        listingAccountId: 9, // Bin Store listingAccountId
        listingId,
      });

      setToggle(true);
      setSubmitting(false);
      resetState();
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } else {
      toast.error("Something went wrong, please try again.");
      setError(response);
      setSubmitting(false);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const validateForm = () => {
    let valid = true;

    // Input validation --------------------------------
    if (!value) {
      setValidationErrors((prevState) => {
        return { ...prevState, input: "Input is required." };
      });
      valid = false;
    }

    return valid;
  };

  const resetState = () => {
    setValue("");
    setError({});
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-start border-bottom">
        <FontAwesomeIcon
          icon={faGavel}
          size="3x"
          className="align-self-center me-3"
        />
        <FontAwesomeIcon
          icon={faArrowRight}
          size="lg"
          className="align-self-center me-3"
        />
        <FontAwesomeIcon
          icon={faStore}
          size="3x"
          className="align-self-center me-3"
        />
        <div>
          <Card.Title>
            Convert an Auction listing to a Bin Store listing.
          </Card.Title>
          <Card.Subtitle>
            Scan or enter a Listing ID, Auction ID, or SKU to convert the
            listing to a Bin Store listing.
          </Card.Subtitle>
        </div>
      </Card.Header>
      <Card.Body>
        {Object.keys(error).length > 0 && (
          <Alert variant="danger" className="flex-fill alert-blink">
            <div className="alert-icon">
              <AlertOctagon
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Form
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Row>
            <Col>
              <Form.Group>
                <Form.Label>Listing ID / Auction ID / SKU</Form.Label>
                <Form.Control
                  ref={inputRef}
                  autoFocus
                  size="lg"
                  placeholder="Enter a Listing ID, Auction ID, or SKU..."
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      handleSubmit();
                    }
                  }}
                  isInvalid={validationErrors.input}
                  disabled={submitting}
                />
                {validationErrors.input && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.input}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" className="align-self-start">
              {/* Adds space above the button for validation errors */}
              <div style={{ height: `${29.45}px` }}></div>
              <Button
                size="lg"
                variant="primary"
                onClick={() => {
                  handleSubmit();
                }}
                disabled={submitting}
              >
                {submitting && (
                  <Spinner
                    className="me-2"
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </Col>
          </Row>
        </Form>
        <Collapse in={!toggle}>
          <div>
            {skuListings.length > 0 && (
              <Card className="border mt-3">
                <Card.Body>
                  <Card.Text>
                    Search for and click on the correct corresponding Listing ID
                    to convert the listing.
                  </Card.Text>
                  <Table
                    columns={[
                      {
                        Header: "Listing ID",
                        accessor: "ListingID",
                      },
                    ]}
                    data={skuListings}
                    onRowClick={(row) => {
                      handleSubmit(row.original.ListingID);
                    }}
                    size={"sm"}
                    initialPageSize={200}
                    layoutFixed={false}
                  />
                </Card.Body>
              </Card>
            )}
          </div>
        </Collapse>
      </Card.Body>
    </Card>
  );
};

export default AuctionToBin;
